// frontend/src/services/authService.js

/**
 * Envía las credenciales del usuario al backend y devuelve el accessToken.
 *
 * @param {string} email - Correo del usuario
 * @param {string} password - Contraseña del usuario
 * @returns {Promise<{ accessToken: string }>}
 * @throws {Error} Si hay error de red o credenciales inválidas
 */
export const loginUser = async (email, password) => {
  const url = `${process.env.REACT_APP_API_BASE}/api/auth/login`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    let message = "Login failed. Please try again.";
    try {
      const error = await response.json();
      if (error?.error) message = error.error;
    } catch (_) {
      // Ignorar error de parseo
    }
    throw new Error(message);
  }

  return await response.json();
};

/**
 * Cierra la sesión del usuario eliminando el token JWT del sessionStorage.
 */
export const logoutUser = () => {
  try {
    clearJWTSession();
  } catch (error) {
    console.error("Error clearing the session:", error);
  }
};

/**
 * Nueva función para solicitar el envío de un enlace mágico.
 * @param {string} identifier - El email o código de acceso del grupo.
 * @returns {Promise<any>}
 * @throws {Error} Si la solicitud falla.
 */
export const sendMagicLink = async (identifier) => {
  const url = `${process.env.REACT_APP_API_BASE}/api/auth/magiclink`;

  const body = identifier.includes('@')
    ? { contact_email: identifier }
    : { group_access_code: identifier };

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    let message = "Failed to send magic link. Please try again.";
    try {
      const error = await response.json();
      if (error?.error) message = error.error;
    } catch (_) {
      // Ignorar error de parseo
    }
    throw new Error(message);
  }

  return await response.json();
};

/**
 * Valida un token de enlace mágico con el backend.
 * @param {string} token - El token de un solo uso.
 * @returns {Promise<{ hash: string, role: 'leader'|'guest', accessToken?: string }>}
 * @throws {Error} Si el token es inválido o ha expirado.
 */
export const validateMagicLink = async (token) => {
  const url = `${process.env.REACT_APP_API_BASE}/api/auth/magiclink/${token}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    let message = "Invalid or expired link.";
    try {
      const error = await response.json();
      if (error?.error) message = error.error;
    } catch (_) {
      // Ignorar error de parseo
    }
    throw new Error(message);
  }

  return await response.json();
};